// packages import
import { Box, TextField } from '@mui/material'
import { FC, useContext, useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'
import { Maybe } from 'graphql/jsutils/Maybe'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'

// component imports
import ClientDropdown from '../../../common/ClientDropdown'
import MatterDropdown from '../../../common/MatterDropdown'

// others
import { Case, ExternalUser } from '../../../../../generated'
import { InterestEntriesContext } from '../../../../context/InterestEntriesContext'

const InterestEntriesFilters: FC = () => {
  const { filters, setFilters } = useContext(InterestEntriesContext)

  const [billIdInput, setBillIdInput] = useState<string>(filters.billId ? String(filters.billId) : '')
  const [debouncedBillId] = useDebounce(billIdInput, 500)

  useEffect(() => {
    const parsed = debouncedBillId ? Number(debouncedBillId) : undefined
    const next = Number.isFinite(parsed) ? parsed : undefined
    if (next === filters.billId) return
    setFilters({ billId: next })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedBillId, setFilters])

  const handleClientChange = (value: Maybe<ExternalUser>) => {
    setFilters({ clientId: value?.id })
  }

  const handleMatterChange = (value: Maybe<Case>) => {
    setFilters({ caseId: value?.id })
  }

  const handleFromDateChange = (value: unknown) => {
    const d = value as Dayjs | null
    setFilters({ fromDate: d && d.isValid() ? d.startOf('day').toISOString() : undefined })
  }

  const handleToDateChange = (value: unknown) => {
    const d = value as Dayjs | null
    setFilters({ toDate: d && d.isValid() ? d.endOf('day').toISOString() : undefined })
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" sx={{ px: 2, pt: 3, pb: 4 }}>
        <TextField
          label="Bill ID"
          value={billIdInput}
          onChange={e => setBillIdInput(e.target.value.replace(/[^0-9]/g, ''))}
          sx={{ width: 140 }}
        />

        <ClientDropdown
          selectedClientId={filters.clientId || ''}
          label="Client"
          sx={{ minWidth: 220 }}
          onChange={handleClientChange}
        />

        <MatterDropdown customOnChange={handleMatterChange} label="Matter" sx={{ width: 260 }} />

        <DatePicker
          label="From Date"
          value={filters.fromDate ? dayjs(filters.fromDate) : null}
          onChange={handleFromDateChange}
          renderInput={params => <TextField {...params} sx={{ width: 180 }} />}
        />

        <DatePicker
          label="To Date"
          value={filters.toDate ? dayjs(filters.toDate) : null}
          onChange={handleToDateChange}
          renderInput={params => <TextField {...params} sx={{ width: 180 }} />}
        />
      </Box>
    </LocalizationProvider>
  )
}

export default InterestEntriesFilters
